import React from 'react';
import {
  Flex,
  Box,
  Heading,
  Text,
  DataTable,
  LoadingOrbit,
  Card,
  CardHeader,
  CardContent,
  Button,
  TextField,
  TagBadge,
} from '@chatsdet/ui';
import { UsersThree } from '@phosphor-icons/react';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import {
  useSuperadminUsersQuery,
  useDeactivateUserMutation,
  useReactivateUserMutation,
  type SuperadminUser,
} from './useSuperadminUserData';

export function SuperadminUsers() {
  const [search, setSearch] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [page, setPage] = React.useState(1);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const usersQuery = useSuperadminUsersQuery({ search: debouncedSearch, page });
  const deactivateMutation = useDeactivateUserMutation();
  const reactivateMutation = useReactivateUserMutation();

  const handleDeactivate = (user: SuperadminUser) => {
    if (
      window.confirm(
        `Deactivate ${user.primaryEmail}?\n\nThey will be logged out of all sessions immediately and won't be able to sign in until reactivated.`
      )
    ) {
      deactivateMutation.mutate(user.id);
    }
  };

  const handleReactivate = (user: SuperadminUser) => {
    reactivateMutation.mutate(user.id);
  };

  const columns: ColumnDef<StockFeatures, SuperadminUser>[] = React.useMemo(
    () => [
      {
        accessorKey: 'primaryEmail',
        header: 'Email',
        cell: ({ row }) => (
          <div>
            <Text weight="medium">{row.original.primaryEmail}</Text>
            {row.original.displayName && (
              <Text size="sm" className="text-muted-foreground">
                {row.original.displayName}
              </Text>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'authProvider',
        header: 'Auth',
        cell: ({ row }) => (
          <TagBadge name={row.original.authProvider} color="gray" hideDot size="sm" />
        ),
      },
      {
        accessorKey: 'isActive',
        header: 'Status',
        cell: ({ row }) =>
          row.original.isActive ? (
            <TagBadge name="Active" color="blue" hideDot size="sm" />
          ) : (
            <TagBadge name="Deactivated" color="red" hideDot size="sm" />
          ),
      },
      {
        accessorKey: 'isSuperadmin',
        header: 'Role',
        cell: ({ row }) =>
          row.original.isSuperadmin ? (
            <TagBadge name="Superadmin" color="yellow" hideDot size="sm" />
          ) : null,
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => (
          <Text className="text-muted-foreground" size="sm">
            {new Date(row.original.createdAt).toLocaleDateString()}
          </Text>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const user = row.original;
          if (user.isSuperadmin) return null;

          const isPending = deactivateMutation.isPending || reactivateMutation.isPending;

          return user.isActive ? (
            <Button
              variant="outline"
              size="sm"
              onPress={() => handleDeactivate(user)}
              isDisabled={isPending}
            >
              Deactivate
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onPress={() => handleReactivate(user)}
              isDisabled={isPending}
            >
              Reactivate
            </Button>
          );
        },
      },
    ],
    [deactivateMutation.isPending, reactivateMutation.isPending]
  );

  const pagination = usersQuery.data?.pagination;

  return (
    <Box className="space-y-6">
      <Card padding="lg">
        <CardHeader>
          <Flex align="center" justify="between" wrap gap="md">
            <Flex align="center" gap="md">
              <div className="p-2 bg-primary/10 rounded-lg">
                <UsersThree size={24} className="text-primary" />
              </div>
              <div>
                <Heading level={3}>Users</Heading>
                <Text size="sm" className="text-muted-foreground mt-1">
                  Manage user accounts across all organizations
                </Text>
              </div>
            </Flex>
            <TextField
              aria-label="Search users"
              placeholder="Search by email or name..."
              value={search}
              onChange={setSearch}
              className="w-72"
            />
          </Flex>
        </CardHeader>
        <CardContent>
          {usersQuery.isLoading && !usersQuery.data ? (
            <Flex align="center" justify="center" className="py-8">
              <LoadingOrbit size="lg" />
            </Flex>
          ) : usersQuery.isError ? (
            <Text className="text-destructive">
              {usersQuery.error instanceof Error
                ? usersQuery.error.message
                : 'Failed to load users'}
            </Text>
          ) : usersQuery.data?.data?.length ? (
            <DataTable
              columns={columns}
              data={usersQuery.data.data}
              serverPagination={
                pagination
                  ? {
                      page: pagination.page,
                      pageSize: pagination.pageSize,
                      pageCount: pagination.totalPages,
                      totalItems: pagination.totalItems,
                      onPageChange: setPage,
                      isLoading: usersQuery.isFetching,
                    }
                  : undefined
              }
            />
          ) : (
            <Text className="text-muted-foreground py-4">
              {debouncedSearch ? 'No users match your search.' : 'No users found.'}
            </Text>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
